/**
 * transitionTemplates — deterministic bubble copy for FSM gate transitions.
 *
 * Each helper returns `{ userBubble, assistantBubble }` — the canonical text
 * strings sent to `POST /v1/dashboards/:id/flow-transition` and appended to
 * the chat thread as `kind: 'template'` messages.
 *
 * This module is a pure formatting helper with zero side-effects so it is
 * easy to unit-test independently of Redux or the service thunk.
 *
 * Spec: plans/2026-04-29-step-5-deterministic-panels-and-chat-context.md
 *       §Slice 1.3 "Templated copy".
 */
import type { DashboardDomainRegistryItem } from '@/types/DashboardDomainRegistry.types'

// ─── Gate A: Source pick (→ awaiting_credential) ──────────────────────────────

export interface SourcePickTemplates {
  userBubble: string
  assistantBubble: string
}

/**
 * @param sourceDisplayName — e.g. "CampMinder"
 */
export function buildSourcePickTemplates(sourceDisplayName: string): SourcePickTemplates {
  return {
    userBubble: sourceDisplayName,
    assistantBubble: `Great choice. To pull your ${sourceDisplayName} data, I need to connect to your account. The easiest way is to log in directly — I'll set up a secure API key automatically.`,
  }
}

// ─── Gate B: Credential pick (→ awaiting_domain) ─────────────────────────────

export interface CredentialPickTemplates {
  userBubble: string
  assistantBubble: string
}

/**
 * @param credentialName    — e.g. "Berkshire Sports Academy"
 * @param sourceDisplayName — e.g. "CampMinder"
 */
export function buildCredentialPickTemplates(
  credentialName: string,
  sourceDisplayName: string
): CredentialPickTemplates {
  return {
    userBubble: `${credentialName} — ${sourceDisplayName}`,
    assistantBubble:
      'Connected. Now, what kind of data do you want this dashboard to cover? You can pick multiple.',
  }
}

// ─── Gate C: Domain pick (→ awaiting_filters) ─────────────────────────────────

export interface DomainPickTemplates {
  userBubble: string
  assistantBubble: string
}

/**
 * Builds the chat bubble copy for the awaiting_domain → awaiting_filters
 * transition. Registry-driven post-Slice 4 of EPIC-132's metadata refactor:
 * each domain's `pickerScopeCopy` is owned by the SDK domain definition (TDD
 * §7.8) — the per-domain scope-copy map that previously lived here was
 * deleted.
 *
 * Single-domain selection uses the chosen domain's `pickerScopeCopy` (or a
 * generic fallback when the field is absent — older registry shapes).
 * Multi-domain selection uses the generic copy (concatenating per-domain
 * scope sentences would be noisy).
 *
 * @param selectedDomains — registry items for the user's selection, in order
 */
export function buildDomainPickTemplates(
  selectedDomains: DashboardDomainRegistryItem[],
): DomainPickTemplates {
  const displayNames = selectedDomains.map(d => d.displayName)
  const domainLabel =
    displayNames.length === 1 ? displayNames[0] : displayNames.join(' + ')

  // Single domain: use the domain label in the assistant narration.
  // Multi-domain: narration stays the same (pick the first for readability).
  // Empty selection (defensive — CTA is disabled at 0 domains, so this path
  // should not occur in production): fall back to a safe sentence that avoids
  // the doubled "your data data" that arises when primaryDomain is 'your data'.
  if (selectedDomains.length === 0) {
    return {
      userBubble: '',
      assistantBubble: `Let's pick the filters you want to include.`,
    }
  }

  const primaryDomain = displayNames[0]

  const scopeCopy =
    selectedDomains.length === 1
      ? selectedDomains[0].pickerScopeCopy ?? 'Pick the filters you want to include.'
      : 'Pick the filters you want to include.'

  return {
    userBubble: domainLabel,
    assistantBubble: `Let's scope your ${primaryDomain} data. ${scopeCopy}`,
  }
}

// ─── Gate D: Filter confirm (→ awaiting_panels) ───────────────────────────────

export interface FilterConfirmTemplates {
  userBubble: string
  assistantBubble: string
}

export interface FilterSummary {
  label: string
  values: string[]
}

/**
 * Summarises the confirmed filter values for the user bubble.
 *
 * @param domainName      — domain display name (e.g. "Enrollment" or
 *                          "Enrollment + Financial" for multi-domain). Used
 *                          verbatim as the leading segment of the user bubble
 *                          and in the assistant narration.
 * @param filterSummaries — ordered list of { label, values } pairs. Entries
 *                          whose `values` array is empty are silently skipped.
 *                          - Zero non-empty entries → just `domainName`
 *                          - One entry   → `"{Domain} — {Label}: {v1, v2}"`
 *                          - Two+ entries → `"{Domain} — {L1}: {v1} · {L2}: {v2} · …"`
 *                          Separator between groups is ` · ` (middot with spaces).
 * @param panelCount      — total number of proposed panels (for assistant
 *                          narration). When omitted or 0 the count clause is
 *                          omitted because panels haven't been fetched yet at
 *                          the moment the flow-transition fires (transition
 *                          precedes panel proposal per §Slice 1.3).
 */
export function buildFilterConfirmTemplates(
  domainName: string,
  filterSummaries: FilterSummary[],
  panelCount?: number
): FilterConfirmTemplates {
  const panelClause =
    panelCount != null && panelCount > 0
      ? `All ${panelCount} recommended panels are checked by default — uncheck anything you don't need, or add more from Explore More below.`
      : `All recommended panels are checked by default — uncheck anything you don't need, or add more from Explore More below.`

  const activeSummaries = filterSummaries.filter(s => s.values.length > 0)

  const userBubble =
    activeSummaries.length === 0
      ? domainName
      : `${domainName} — ${activeSummaries
          .map(s => `${s.label}: ${s.values.join(', ')}`)
          .join(' · ')}`

  return {
    userBubble,
    assistantBubble: `Here's the full panel catalog for your ${domainName} dashboard. ${panelClause}`,
  }
}
